import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { stacks } from '../data/stacks';

// Quick-view bottom sheet (mobile) / centred modal (tablet+) — tap a stack
// card, this slides up over the current screen with just enough to decide:
// name, what it's for, an ingredients summary, price, and the two buy
// buttons. No scrolling the page, no navigating away. Same pattern as any
// basic Shopify "quick add" modal.
function StackQuickView({ stack, onClose }) {
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const billingLabel = stack.billingPeriod === 'monthly' ? 'month' : stack.billingPeriod.replace('every ', '');
  const ingredientsList = stack.ingredients.split(',').map((s) => s.trim());
  const ingredientsSummary = ingredientsList.slice(0, 3).join(', ')
    + (ingredientsList.length > 3 ? `, +${ingredientsList.length - 3} more` : '');

  return (
    <div
      className="stack-modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="presentation"
    >
      <div className="stack-modal" role="dialog" aria-modal="true" aria-label={stack.name}>
        <button className="stack-modal__close" onClick={onClose} aria-label="Close">×</button>

        <div className="stack-modal__badge">{stack.badge}</div>
        <h2 className="stack-modal__name">{stack.name}</h2>
        <p className="stack-modal__for">{stack.for}</p>

        <div className="stack-modal__ingredients">
          <span className="stack-modal__ingredients-label">Ingredients</span>
          {ingredientsSummary}
        </div>

        <div className="stack-modal__price">
          £{stack.subscriptionPrice} <span>/{billingLabel} · subscribe and save</span>
        </div>

        <a
          href={stack.stripeSubscription}
          target="_blank"
          rel="noopener noreferrer"
          className="stack-modal__btn stack-modal__btn--primary"
        >
          Subscribe — £{stack.subscriptionPrice}/{billingLabel}
        </a>
        <a
          href={stack.stripeOneOff}
          target="_blank"
          rel="noopener noreferrer"
          className="stack-modal__btn stack-modal__btn--secondary"
        >
          Buy Once — £{stack.price}
        </a>

        <p className="stack-modal__disclaimer">
          Food supplement — not a replacement for a varied diet. Consult your GP before use if pregnant, breastfeeding or taking medication.
        </p>
      </div>
    </div>
  );
}

export default function Stacks() {
  const [selected, setSelected] = useState(null);
  const stack = stacks.find((s) => s.slug === selected);

  return (
    <>
      <Helmet defer={false}>
        <title>Supplement Stacks | Hormonely</title>
        <meta name="description" content="Eight food supplements, made in the UK under GMP standards. One-off purchase or subscription, free UK delivery, cancel any time." />
      </Helmet>

      <div className="page-hero">
        <div className="container page-hero__inner">
          <div className="eyebrow" style={{ color: 'var(--clay-lt)', marginBottom: '16px' }}>Stacks by Hormonely</div>
          <h1 className="page-hero__title">Stacks by <em>Hormonely.</em></h1>
          <p className="page-hero__sub">Eight food supplements, made in the UK under GMP standards. No minimum orders, delivered on subscription.</p>
        </div>
      </div>

      <section className="section section--warm">
        <div className="container">

          {/* Trust bar */}
          <div style={{ display: 'flex', gap: '24px', marginBottom: '40px', flexWrap: 'wrap' }}>
            {['Free UK delivery on every order', 'No minimum orders', 'Cancel any time', 'UK-made under GMP standards'].map((f, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'var(--clay)', flexShrink: 0 }}></div>
                <span style={{ fontSize: '13px', fontWeight: 500, color: 'var(--stone)' }}>{f}</span>
              </div>
            ))}
          </div>

          {/* Stack grid — tapping a card opens the quick-view modal below,
              it never expands or reveals anything inline in the grid itself */}
          <div className="stacks-grid">
            {stacks.map((s) => (
              <button
                key={s.slug}
                onClick={() => setSelected(s.slug)}
                style={{
                  background: 'var(--white)',
                  border: '1px solid rgba(44,34,24,0.07)',
                  borderRadius: '12px',
                  padding: '28px 24px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  fontFamily: 'var(--sans)',
                }}
              >
                <div style={{
                  display: 'inline-block',
                  fontSize: '10px',
                  fontWeight: 600,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  padding: '3px 8px',
                  borderRadius: '4px',
                  background: 'rgba(107,124,110,0.12)',
                  color: 'var(--sage)',
                  marginBottom: '12px',
                }}>{s.badge}</div>
                <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--bark)', marginBottom: '6px' }}>{s.name}</div>
                <div style={{ fontSize: '13px', color: 'var(--stone)', marginBottom: '16px', lineHeight: 1.5 }}>{s.for}</div>
                <div style={{ fontFamily: 'var(--serif)', fontSize: '22px', color: 'var(--clay)' }}>
                  £{s.subscriptionPrice} <span style={{ fontFamily: 'var(--sans)', fontSize: '12px', color: 'var(--stone)' }}>/{s.billingPeriod === 'monthly' ? 'month' : s.billingPeriod.replace('every ', '')}</span>
                </div>
              </button>
            ))}
          </div>

          {stack && <StackQuickView stack={stack} onClose={() => setSelected(null)} />}

          <div style={{ marginTop: '48px', background: 'var(--white)', borderRadius: '12px', padding: '32px', maxWidth: '720px' }}>
            <div className="eyebrow" style={{ marginBottom: '16px' }}>How it works</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              {[
                { n: '01', t: 'Find your stack', b: 'Tap any stack above to see full details and buy options.' },
                { n: '02', t: 'Subscribe and save', b: 'Subscribe and save vs a one-off purchase. Cancel any time.' },
                { n: '03', t: 'Dispatched to your door', b: 'Free UK delivery on every order. No minimum orders.' },
                { n: '04', t: 'UK-made, quality assured', b: 'All products manufactured in the UK under GMP and ISO standards.' },
              ].map((s, i) => (
                <div key={i}>
                  <div style={{ fontFamily: 'var(--serif)', fontSize: '32px', color: 'var(--clay-lt)', marginBottom: '8px' }}>{s.n}</div>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--bark)', marginBottom: '6px' }}>{s.t}</div>
                  <div style={{ fontSize: '13px', color: 'var(--stone)', lineHeight: 1.5 }}>{s.b}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
